import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';

/**
 * FM Dashboard authorization — copy-and-own port of the GRM shared `authorizationService`
 * (`js/app/shared/services/authorization.service.js`). PRD §9 allows copy-and-own for
 * shared-module pieces that must not be imported across repos.
 *
 * Parity map:
 * - `parseTenantClaims`        ← `performAuthorization()`      `:136-155`
 * - `hasResourcePermission`    ← `hasResourcePermission()`     `:95-124`
 * - `hasRole`                  ← `hasRole()`                   `:85-93`
 * - `isResourceManager`        ← `isResourceManager()`         `:197-208`
 * - `hasResourceSearchAccess`  ← `hasResourceSearchAccess()`   `:273-283`
 * - `hasTeamCalendarAccess`    ← `hasTeamCalendarAccess()`     `:285-295`
 * - `resolveCalendarRole`      ← `fmdashboard.controller.js OpenCalendar()` `:204-216`
 *
 * Everything here is DEFAULT-DENY: if claims cannot be resolved (no FXP shell, malformed
 * payload, thrown error) every capability is false, so we never render a cross-team link for a
 * user who may not be allowed to follow it.
 */

/** Parity of `GrmConfiguration.PermissionMappingDictionary` (`grm.config.prod.json:178-184`). */
export const PERMISSION_MAPPING: Record<string, string[]> = {
  All: [
    'Add',
    'Edit',
    'View',
    'Read_Team',
    'Read_All',
    'Write_Team',
    'Write_All',
    'Accrediation_Override',
    'Request',
    'Approve',
  ],
  Read_All: ['View', 'Read_Team'],
  Read_Team: ['View'],
  Write_All: ['Add', 'Edit', 'Write_Team'],
  Write_Team: ['Add', 'Edit'],
};

export interface FMResourcePermission {
  resource: string;
  permissions: string[];
}

export interface FMRole {
  roleName?: string | null;
}

export interface FMUserClaims {
  Roles: Record<string, FMRole>;
  ResourcePermissions: FMResourcePermission[];
}

/** Role segment of the `grm-resourcedashboardfm` deep link. */
export type CalendarRole = 'ResourceManager' | 'FunctionalManager';

export interface FMPermissions {
  isResourceManager: boolean;
  hasTeamCalendarAccess: boolean;
  hasResourceSearchAccess: boolean;
  /** Role segment for the resource-calendar deep link. `undefined` ⇒ do not render the link. */
  calendarRole?: CalendarRole;
}

export const DENY_ALL: FMPermissions = {
  isResourceManager: false,
  hasTeamCalendarAccess: false,
  hasResourceSearchAccess: false,
};

const TENANTS = ['PS', 'FxPPlatform'];

/**
 * Merge the `PS` and `FxPPlatform` tenant claim bags into a single roles/permissions view,
 * exactly as the legacy service does before evaluating any capability.
 */
export function parseTenantClaims(tenantClaims: unknown): FMUserClaims | undefined {
  if (!tenantClaims || typeof tenantClaims !== 'object') {
    return undefined;
  }
  const bag = tenantClaims as Record<string, { claims?: { roles?: Record<string, FMRole>; resourcePermissions?: FMResourcePermission[] } }>;
  const roles: Record<string, FMRole> = {};
  let permissions: FMResourcePermission[] = [];
  let sawTenant = false;

  TENANTS.forEach((tenant) => {
    const claims = bag[tenant] && bag[tenant].claims;
    if (!claims) {
      return;
    }
    sawTenant = true;
    Object.keys(claims.roles || {}).forEach((role) => {
      roles[role] = (claims.roles as Record<string, FMRole>)[role];
    });
    permissions = permissions.concat(claims.resourcePermissions || []);
  });

  return sawTenant ? { Roles: roles, ResourcePermissions: permissions } : undefined;
}

/**
 * `resource`/`permission` check with the legacy's three escape hatches, in order:
 * a `ResourceManagement:All` super-permission, a direct match, then the mapping dictionary
 * (e.g. a user holding `Read_All` also satisfies a `Read_Team` requirement).
 */
export function hasResourcePermission(
  claims: FMUserClaims | undefined,
  resource: string,
  permission: string
): boolean {
  if (!claims || !Array.isArray(claims.ResourcePermissions) || !resource || !permission) {
    return false;
  }
  const held = claims.ResourcePermissions;

  const isAdmin = held.some(
    (p) => p && p.resource === 'ResourceManagement' && (p.permissions || []).indexOf('All') > -1
  );
  if (isAdmin) {
    return true;
  }

  const match = held.filter((p) => p && p.resource === resource)[0];
  if (!match) {
    return false;
  }
  const mine = match.permissions || [];
  if (mine.indexOf(permission) > -1) {
    return true;
  }
  return mine.some(
    (m) => (PERMISSION_MAPPING[m] || []).indexOf(permission) > -1
  );
}

export function hasRole(claims: FMUserClaims | undefined, role: string): boolean {
  if (!claims || !claims.Roles || !role) {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(claims.Roles, role);
}

export function isResourceManager(claims: FMUserClaims | undefined): boolean {
  return (
    hasRole(claims, 'ResourceManagement') &&
    !!claims &&
    !!claims.Roles.ResourceManagement &&
    claims.Roles.ResourceManagement.roleName != null
  );
}

/** `ProjectAssignment:Read_All` AND `NonProjectAssignment:Read_All`. */
export function hasResourceSearchAccess(claims: FMUserClaims | undefined): boolean {
  return (
    hasResourcePermission(claims, 'ProjectAssignment', 'Read_All') &&
    hasResourcePermission(claims, 'NonProjectAssignment', 'Read_All')
  );
}

/** `ProjectAssignment:Read_Team` AND `NonProjectAssignment:Read_Team`. */
export function hasTeamCalendarAccess(claims: FMUserClaims | undefined): boolean {
  return (
    hasResourcePermission(claims, 'ProjectAssignment', 'Read_Team') &&
    hasResourcePermission(claims, 'NonProjectAssignment', 'Read_Team')
  );
}

/**
 * Legacy `OpenCalendar` picks the role rather than assuming one, and refuses to navigate when
 * neither capability is held. `undefined` here means "do not render the Calendar link".
 */
export function resolveCalendarRole(claims: FMUserClaims | undefined): CalendarRole | undefined {
  if (isResourceManager(claims)) {
    return 'ResourceManager';
  }
  if (hasTeamCalendarAccess(claims)) {
    return 'FunctionalManager';
  }
  return undefined;
}

export function toFMPermissions(claims: FMUserClaims | undefined): FMPermissions {
  if (!claims) {
    return DENY_ALL;
  }
  return {
    isResourceManager: isResourceManager(claims),
    hasTeamCalendarAccess: hasTeamCalendarAccess(claims),
    hasResourceSearchAccess: hasResourceSearchAccess(claims),
    calendarRole: resolveCalendarRole(claims),
  };
}

interface FxpUserClaimsService {
  getUserTenantClaims?: () => unknown;
}

/**
 * Resolve the signed-in user's FM capabilities from the FXP shell. Never throws; returns
 * `DENY_ALL` when the shell or the claims payload is unavailable.
 */
export function getFMPermissions(): FMPermissions {
  try {
    const service = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.UserClaimsService
    ) as FxpUserClaimsService;
    const tenantClaims = service && service.getUserTenantClaims && service.getUserTenantClaims();
    return toFMPermissions(parseTenantClaims(tenantClaims));
  } catch {
    return DENY_ALL;
  }
}
